import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX, FiExternalLink } = FiIcons;

function AboutModal({ isOpen, onClose }) {
  if (!isOpen) return null;

  const phases = [
    {
      title: "Pre-Alphabetic Phase",
      description: "Children recognize words using visual cues or context, not letter-sound relationships. They might recognize logos or environmental print.",
      color: "from-brand-sage-light/30 to-brand-sage-light/10",
      textColor: "text-brand-dark-blue"
    },
    {
      title: "Partial Alphabetic Phase", 
      description: "Children begin using some letter-sound connections, typically the first and last letters. They may recognize \"dog\" by the 'd' and 'g' but miss the middle sound.",
      color: "from-brand-teal-light/20 to-brand-teal-light/5",
      textColor: "text-brand-dark-blue"
    },
    {
      title: "Full Alphabetic Phase",
      description: "Children can use all letter-sound relationships to decode unfamiliar words. They can sound out CVC words and blend the sounds together.", 
      color: "from-brand-peach-light/40 to-brand-peach-light/10",
      textColor: "text-brand-dark-blue"
    },
    {
      title: "Consolidated Alphabetic Phase",
      description: "Children recognize common spelling patterns and larger units (chunks, syllables). Reading becomes more automatic and fluent.",
      color: "from-brand-mauve-light/30 to-brand-mauve-light/10", 
      textColor: "text-brand-dark-blue"
    },
    {
      title: "Automatic Phase",
      description: "Children read most words automatically by sight, allowing them to focus on comprehension rather than decoding.",
      color: "from-brand-sage-light/40 to-brand-peach-light/20",
      textColor: "text-brand-dark-blue"
    }
  ];

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.3 }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="sticky top-0 bg-white rounded-t-2xl border-b border-brand-sage/20 p-6 flex justify-between items-start">
            <h3 className="text-2xl font-abril text-brand-dark-blue">
              Ehri's Phases of Reading Development
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 hover:bg-gray-100 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-teal/30"
            >
              <SafeIcon icon={FiX} className="h-6 w-6" />
            </button>
          </div>

          <div className="p-6 space-y-6">
            <motion.p
              className="text-black leading-relaxed text-lg"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              Linnea Ehri's phases of reading development provide a framework for understanding how children learn to read words. These phases describe the predominant strategy a reader uses to recognize words.
            </motion.p>

            <div className="space-y-4">
              {phases.map((phase, index) => (
                <motion.div
                  key={phase.title}
                  className={`bg-gradient-to-r ${phase.color} p-6 rounded-xl border border-brand-sage/20`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.2 + index * 0.1 }}
                >
                  <h4 className={`font-abril ${phase.textColor} text-xl mb-3`}>
                    {phase.title}
                  </h4>
                  <p className="text-black leading-relaxed">
                    {phase.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.div
              className="bg-gradient-to-r from-brand-sage-light/30 to-brand-peach-light/20 p-6 rounded-xl border border-brand-sage/30"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <p className="text-black leading-relaxed">
                For more information about reading development and strategies, visit our{' '}
                <a
                  href="https://thriveedservices.com/blog/"
                  className="text-brand-teal hover:text-brand-teal-dark font-semibold inline-flex items-center gap-1 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  blog
                  <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
                </a>
                {' '}or explore our{' '}
                <a
                  href="https://thriveedservices.com/freebies/"
                  className="text-brand-teal hover:text-brand-teal-dark font-semibold inline-flex items-center gap-1 transition-colors"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  freebies
                  <SafeIcon icon={FiExternalLink} className="h-4 w-4" />
                </a>
                .
              </p>
            </motion.div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

export default AboutModal;